import { datadogRum } from '@datadog/browser-rum';
import {
  useFlags,
  useInitializationStatus,
  useLDClient,
} from '@launchdarkly/react-sdk';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import LandingPage from '@/components/LandingPage/LandingPage';
import SimpleLoader from '@/components/Loader/SimpleLoader';
import { COMPANY_NAME } from '@/constants/constants';
import { Meta } from '@/layouts/Meta';
import type { FeatureFlags } from '@/models/Types';
import { CenteredContainer } from '@/styles/shared';
import { Main } from '@/templates/Main';
import { isAnonymousLaunchDarklyKeyPersisted } from '@/utils/anonymousLaunchDarklyKey';
import { AppConfig } from '@/utils/AppConfig';
import { readFlowSessionPartnerCode } from '@/utils/dtiFlowSessionPartner';
import {
  getV3DtiGateMode,
  isAppRouterPagesEnabled,
} from '@/utils/FeatureFlags';
import { flushThenNavigate } from '@/utils/flushLaunchDarklyEvents';
import {
  isExcludedCloseknitReferral,
  resolveLegacyChasDtiRedirect,
} from '@/utils/legacyChasDtiRedirect';
import { isRouterReady } from '@/utils/routerReadiness';

export async function getStaticProps() {
  // Make the landing page background brighter/whiter.
  return { props: { altBackground: true } };
}

const Index = () => {
  const flags = useFlags<FeatureFlags>();
  const { bypassGetStarted = false, enableDtiV3 = false } = flags;

  const router = useRouter();
  const { status: launchDarklyStatus } = useInitializationStatus();
  const launchDarklyComplete = launchDarklyStatus === 'complete';
  const shouldBypassGetStarted = bypassGetStarted && launchDarklyComplete;
  const ldClient = useLDClient();
  // Multi-kind contexts carry no top-level string key, so anything but a
  // string means there is no single anonymous key to compare against.
  const ldContextKey = ldClient?.getContext()?.key;
  const activeLaunchDarklyKey =
    typeof ldContextKey === 'string' ? ldContextKey : null;
  const hostname =
    typeof window === 'undefined' ? '' : window.location.hostname;
  const chasRedirectUrl = isRouterReady(router)
    ? resolveLegacyChasDtiRedirect(router.query, hostname, flags)
    : null;
  const isExcludedCloseknit = isExcludedCloseknitReferral(router.query, flags);

  useEffect(() => {
    // Skip redirect logic during Cypress tests
    if (typeof window !== 'undefined' && (window as any).Cypress) {
      return;
    }

    // Wait for Next.js to hydrate query params before redirect logic
    if (!isRouterReady(router)) {
      return;
    }

    // After a start timeout the SDK keeps fetching; flags that land later must not
    // yank a visitor off a page they are already using. Status stays 'timeout'.
    if (!launchDarklyComplete) {
      return;
    }
    // Partner-attributed visitors are excluded because `/` evaluates the
    // rollout flag without partnerCode while `/book` evaluates it with one,
    // so handing off could cross LaunchDarkly bucket boundaries. The
    // noPagesToV3 lint rule requires this session read to use a cross-router
    // utility instead of importing `/book` directly.
    const hasPartnerAttribution =
      !!router.query?.partner ||
      !!router.query?.partner_code ||
      !!readFlowSessionPartnerCode();
    const shouldRouteToBook =
      enableDtiV3 &&
      getV3DtiGateMode() !== 'disabled' &&
      isAppRouterPagesEnabled() &&
      // Fail closed when the anonymous key cannot survive navigation.
      isAnonymousLaunchDarklyKeyPersisted(activeLaunchDarklyKey) &&
      !hasPartnerAttribution &&
      !isExcludedCloseknit;

    // The landing options stay clickable during the flush wait, and Next keeps
    // / mounted until the clicked route loads, so the effect cleanup alone runs
    // too late: a route change the visitor starts must cancel the redirect.
    const cancelOnRouteChange = (cancel: () => boolean) => {
      router.events.on('routeChangeStart', cancel);
      return () => {
        router.events.off('routeChangeStart', cancel);
        cancel();
      };
    };

    // Flush before any hard navigation: the enableDtiV3 evaluation above is
    // otherwise unloaded with the page and never reaches LaunchDarkly.
    if (shouldRouteToBook) {
      // Hard navigation with history replace: Back must not land on / and
      // bounce here again. location.search carries the original query verbatim;
      // capture it now so the flush wait cannot change the destination.
      const bookUrl = `/book/${window.location.search}`;
      return cancelOnRouteChange(
        flushThenNavigate(
          () => ldClient.flush(),
          () => window.location.replace(bookUrl),
        ),
      );
    }

    if (chasRedirectUrl) {
      return cancelOnRouteChange(
        flushThenNavigate(
          () => ldClient.flush(),
          () => {
            window.location.href = chasRedirectUrl;
          },
        ),
      );
    } else if (bypassGetStarted) {
      // If not redirecting to CHAS, check if we should bypass landing page
      router.push({
        pathname: '/direct-to-initial/schedule',
        query: router.query,
      });
      datadogRum.addAction('bypassGetStarted', {
        feature: 'launchDarkly',
        environment: AppConfig.env,
      });
    }
    return undefined;
  }, [
    launchDarklyComplete,
    bypassGetStarted,
    enableDtiV3,
    router,
    chasRedirectUrl,
    isExcludedCloseknit,
    router.query,
    activeLaunchDarklyKey,
    ldClient,
    router.isReady,
  ]);

  return (
    <Main
      meta={
        <Meta
          title={`${COMPANY_NAME} - Let's Get Started`}
          description="Schedule your first appointment."
        />
      }
    >
      {shouldBypassGetStarted || (launchDarklyComplete && chasRedirectUrl) ? (
        <CenteredContainer data-testid="simple-loader">
          <SimpleLoader color="green" />
        </CenteredContainer>
      ) : null}
      {!shouldBypassGetStarted ? <LandingPage /> : null}
    </Main>
  );
};

export default Index;
